// Tiny bevelled pixel button for in-canvas controls (hold toggles, nudge arrows).
import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { PALETTE, type PaletteKey } from '../../art/palette';
import { textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';

export interface ButtonStyle {
  face: PaletteKey;
  light: PaletteKey;
  dark: PaletteKey;
  text: PaletteKey;
}

export const STYLES: Record<string, ButtonStyle> = {
  normal: { face: 'paper', light: 'white', dark: 'steel', text: 'ink' },
  active: { face: 'mint', light: 'ice', dark: 'neonTeal', text: 'ink' },
  disabled: { face: 'dusk', light: 'lilacGrey', dark: 'night', text: 'plumGrey' },
  danger: { face: 'red', light: 'coral', dark: 'blood', text: 'white' },
};

export class PixelButton extends Container {
  private bg = new Graphics();
  private caption = new Sprite();
  private icon: Sprite | null = null;
  private pressed = false;
  private hovered = false;
  enabled = true;
  active = false;
  onClick: () => void = () => {};
  onHover: (on: boolean) => void = () => {};

  constructor(
    readonly w: number,
    readonly h: number,
    text: string | null,
    icon?: Texture,
  ) {
    super();
    this.addChild(this.bg, this.caption);
    if (icon) {
      this.icon = new Sprite(icon);
      this.icon.anchor.set(0.5);
      this.addChild(this.icon);
    }
    this.setText(text);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointerdown', () => {
      if (!this.enabled) return;
      this.pressed = true;
      this.redraw();
    });
    this.on('pointerup', () => {
      if (!this.enabled || !this.pressed) return;
      this.pressed = false;
      this.redraw();
      audio.play('click');
      this.onClick();
    });
    this.on('pointerupoutside', () => {
      this.pressed = false;
      this.redraw();
    });
    this.on('pointerover', () => {
      this.hovered = true;
      if (this.enabled) audio.play('hover', { volume: 0.4 });
      this.onHover(true);
      this.redraw();
    });
    this.on('pointerout', () => {
      this.hovered = false;
      this.pressed = false;
      this.onHover(false);
      this.redraw();
    });
    this.redraw();
  }

  private text: string | null = null;
  setText(text: string | null): void {
    this.text = text;
    this.redraw();
  }

  set(enabled: boolean, active = this.active): void {
    if (enabled === this.enabled && active === this.active) return;
    this.enabled = enabled;
    this.active = active;
    this.cursor = enabled ? 'pointer' : 'default';
    this.redraw();
  }

  private redraw(): void {
    const st = !this.enabled ? STYLES.disabled! : this.active ? STYLES.active! : STYLES.normal!;
    const down = this.pressed ? 1 : 0;
    const g = this.bg.clear();
    g.rect(0, 0, this.w, this.h).fill(PALETTE.ink);
    g.rect(1, 1 + down, this.w - 2, this.h - 2 - down).fill(PALETTE[st.face]);
    if (!this.pressed) {
      g.rect(1, 1, this.w - 2, 1).fill(PALETTE[st.light]);
      g.rect(1, this.h - 2, this.w - 2, 1).fill(PALETTE[st.dark]);
    }
    if (this.hovered && this.enabled) g.rect(1, 1 + down, this.w - 2, 1).fill(PALETTE.gold);
    if (this.text !== null) {
      this.caption.texture = textTexture(this.text, st.text, null);
      this.caption.position.set(Math.round((this.w - this.caption.width) / 2), Math.round((this.h - 7) / 2) + down);
    }
    if (this.icon) {
      this.icon.position.set(Math.round(this.w / 2), Math.round(this.h / 2) + down);
      this.icon.alpha = this.enabled ? 1 : 0.4;
    }
  }
}
